import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Icon } from "@/components/ui/Icon";
import { Button } from "@/components/ui/button";

import { useAccounts } from "@/hooks/useAccounts";
import { useAuth } from "@/hooks/useAuth";
import { useAutoTrading } from "@/hooks/useAutoTrading";
import { useReferral } from "@/hooks/useReferral";
import { useTonUsdtInvoices } from "@/hooks/useTonUsdtInvoiceStatus";
import { buildReferralLink } from "@/lib/referralCode";
import { useAccountStore } from "@/store/accountStore";

import { AddApiKeyDialog } from "@/components/AddApiKeyDialog";
import { Header } from "@/components/Header";
import {
  ApiKeysSection,
  LogoutConfirmDialog,
  ReferralCard,
  // TradingSettingsSection,
  UserInfo,
  type ApiKey,
} from "@/components/profile";
import { ActiveSessionsBlock } from "@/components/profile/ActiveSessionsBlock";
import { DevicesSheet } from "@/components/profile/DevicesSheet";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ROUTES } from "@/constants/routes";

const topUpStatusLabels = {
  confirmed: "Зачислено",
  confirming: "Подтверждаем",
  detected: "Перевод найден",
  expired: "Истёк",
  cancelled: "Реквизиты отменены",
  manual_review: "Нужна проверка",
  pending: "Ожидается",
  rejected: "Нужна проверка",
} as const;

export const ProfilePage = () => {
  const { user, logout } = useAuth();
  const {
    accounts,
    isLoading,
    isError: isAccountsError,
    error: accountsError,
    deleteAccountAsync,
    isDeleting,
    createAccountAsync,
    isCreating,
    updateAccountAsync,
    isUpdating,
  } = useAccounts();

  const { selectedAccountUuid, setSelectedAccountUuid } = useAccountStore();
  const {
    status: autoTradingStatus,
    updateAccountAsync: updateAutoAccount,
    isUpdatingAccount,
    refetch,
  } = useAutoTrading();
  const { data: referral, isLoading: isReferralLoading } = useReferral();
  const { data: invoicePages, isLoading: isTopUpsLoading } = useTonUsdtInvoices();
  const recentTopUps = invoicePages?.pages.flatMap((page) => page.items).slice(0, 3) ?? [];

  // Sync selected account from auto-trading status on mount
  useEffect(() => {
    if (autoTradingStatus?.account_uuid && !selectedAccountUuid) {
      setSelectedAccountUuid(autoTradingStatus.account_uuid);
    }
  }, [autoTradingStatus?.account_uuid, selectedAccountUuid, setSelectedAccountUuid]);

  const accountsList = accounts ?? [];

  // Convert accounts to ApiKey format
  const apiKeys: ApiKey[] = accountsList.map((account) => ({
    id: account.uuid,
    name: account.name,
    exchange: "okx" as const,
    isActive: account.uuid === selectedAccountUuid,
  }));

  // Dialog state
  const [isAddApiDialogOpen, setIsAddApiDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<{ id: string; name: string }>();
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isDevicesSheetOpen, setIsDevicesSheetOpen] = useState(false);
  const [deleteApiDialog, setDeleteApiDialog] = useState<{ id: string; name: string } | null>(null);

  // Handlers
  const handleLogoutClick = () => {
    setIsLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    setIsLogoutDialogOpen(false);
    logout();
  };

  const handleSelectApiKey = async (id: string) => {
    if (id === selectedAccountUuid) return;

    const prev = selectedAccountUuid;
    setSelectedAccountUuid(id);

    try {
      await updateAutoAccount(id);
    } catch (e) {
      // откат
      setSelectedAccountUuid(prev ?? null);
      toast.error(e instanceof Error ? e.message : "Не удалось переключить аккаунт");
    }
  };

  const handleEditApiKey = (id: string) => {
    const account = accountsList.find((acc) => acc.uuid === id);
    if (!account) {
      toast.error("Аккаунт не найден");
      return;
    }
    setEditingKey({ id: account.uuid, name: account.name });
    setIsAddApiDialogOpen(true);
  };

  const handleSaveApiKey = async (data: {
    name: string;
    apiKey: string;
    apiSecret: string;
    passphrase: string;
    demo: boolean;
  }) => {
    if (editingKey) {
      await updateAccountAsync({
        uuid: editingKey.id,
        data: {
          name: data.name,
          api_key: data.apiKey,
          api_secret: data.apiSecret,
          passphrase: data.passphrase,
          demo: data.demo,
        },
      });

      toast.success("Аккаунт обновлён");
      setEditingKey(undefined);
      return;
    }

    const created = await createAccountAsync({
      name: data.name,
      api_key: data.apiKey,
      api_secret: data.apiSecret,
      passphrase: data.passphrase,
      demo: data.demo,
    });

    toast.success("Аккаунт успешно создан");

    if (!selectedAccountUuid) {
      setSelectedAccountUuid(created.uuid);
      try {
        await updateAutoAccount(created.uuid);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Создано, но не удалось активировать аккаунт");
      }
    }
  };

  const handleDeleteApiKey = async (id: string) => {
    const account = accountsList.find((acc) => acc.uuid === id);
    if (!account) return;

    setDeleteApiDialog({ id: account.uuid, name: account.name });
  };

  const handleAddApiKey = () => {
    setEditingKey(undefined);
    setIsAddApiDialogOpen(true);
  };

  const handleAddDialogOpenChange = (open: boolean) => {
    setIsAddApiDialogOpen(open);
    if (!open) setEditingKey(undefined);
  };

  return (
    <div className="min-h-screen bg-neutral-950">
      <Header variant="profile" />

      {/* Content - responsive padding and max-width */}
      <main className="mx-auto flex w-full max-w-142 flex-col gap-10 px-4 py-6 md:px-0 md:py-10">
        <div className="flex flex-col gap-4">
          {/* User Info */}
          <UserInfo
            name={user?.username ?? ""}
            email={user?.email ?? ""}
            onLogout={handleLogoutClick}
          />

          {/* Active Sessions */}
          <ActiveSessionsBlock onOpenDevices={() => setIsDevicesSheetOpen(true)} />
        </div>

        {/* Instruction buttons */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Button
            asChild
            variant="neutral"
            size="sm"
            className="w-full"
          >
            <Link to={ROUTES.shortHelp}>
              <Icon
                name="book"
                className="size-5"
              />
              Мини-инструкция
            </Link>
          </Button>
          <Button
            asChild
            variant="secondary"
            size="sm"
            className="w-full"
          >
            <Link to={ROUTES.help}>
              <Icon
                name="book"
                className="size-5"
              />
              Подробная инструкция
            </Link>
          </Button>
        </div>

        {/* Trading Settings */}
        {/* Временно скрыта секция «Уровень риска». */}
        {/* <div className="flex flex-col gap-2">
          <TradingSettingsSection />
        </div> */}

        {(isReferralLoading || referral) && (
          <ReferralCard
            link={referral ? buildReferralLink(referral.referral_code) : ""}
            fuelRewardPercent={referral?.fuel_reward_percent ?? 0}
            invitedCount={referral?.invited_count ?? 0}
            earnedFuel={referral?.earned_fuel ?? "0"}
            isLoading={isReferralLoading}
          />
        )}

        {isAccountsError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {accountsError instanceof Error
              ? accountsError.message
              : "Не удалось загрузить аккаунты. Попробуйте обновить страницу."}
          </div>
        )}

        {/* API Keys */}
        <ApiKeysSection
          apiKeys={apiKeys}
          onSelect={handleSelectApiKey}
          onEdit={handleEditApiKey}
          onDelete={handleDeleteApiKey}
          onAdd={handleAddApiKey}
          isLoading={isLoading}
          isDeleting={isDeleting || isUpdatingAccount}
        />

        <section className="rounded-mds border border-neutral-50/7 bg-neutral-50/2 p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="fs-500 font-bold text-neutral-50">Пополнения</h2>
            </div>
            <Button
              asChild
              variant="neutral"
              size="sm"
            >
              <Link to={ROUTES.topUps}>Все</Link>
            </Button>
          </div>

          <div className="mt-4 divide-y divide-neutral-50/7">
            {isTopUpsLoading && <p className="fs-300 py-4 text-neutral-50/40">Загружаем…</p>}
            {!isTopUpsLoading && recentTopUps.length === 0 && (
              <p className="fs-300 py-4 text-neutral-50/40">Пополнений пока нет.</p>
            )}
            {recentTopUps.map((invoice) => (
              <div
                key={invoice.id}
                className="flex items-center justify-between gap-4 py-3"
              >
                <div className="min-w-0">
                  <p className="fs-300 font-semibold text-neutral-50">
                    {invoice.amount_usdt} USDT → {invoice.credited_fuel} топлива
                  </p>
                  <p className="fs-200 mt-1 truncate font-mono text-neutral-50/40">
                    {invoice.reference}
                  </p>
                </div>
                <span className="fs-200 shrink-0 font-semibold text-neutral-50/60">
                  {topUpStatusLabels[invoice.status]}
                </span>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Add API Key Dialog */}
      <AddApiKeyDialog
        open={isAddApiDialogOpen}
        onOpenChange={handleAddDialogOpenChange}
        onSave={handleSaveApiKey}
        editingKey={editingKey}
        isSaving={isCreating || isUpdating}
      />

      {/* Logout Confirm Dialog */}
      <LogoutConfirmDialog
        open={isLogoutDialogOpen}
        onOpenChange={setIsLogoutDialogOpen}
        onConfirm={handleLogoutConfirm}
      />

      {/* Devices Sheet */}
      <DevicesSheet
        open={isDevicesSheetOpen}
        onOpenChange={setIsDevicesSheetOpen}
      />

      {/* Delete ApiKey Confirm Dialog */}
      <ConfirmDialog
        open={!!deleteApiDialog}
        onOpenChange={(open) => {
          if (!open) setDeleteApiDialog(null);
        }}
        title="Подтвердите действие"
        description={deleteApiDialog ? `Удаление API-ключа “${deleteApiDialog.name}”` : undefined}
        isLoading={isDeleting}
        cancelButton={{
          variant: "secondary",
          className: "order-2",
          children: (
            <>
              <Icon
                name="cancel-circle"
                className="size-5"
              />
              Отмена
            </>
          ),
          onClick: () => setDeleteApiDialog(null),
        }}
        confirmButton={{
          variant: "destructive",
          className: "order-1",
          children: (
            <>
              <Icon
                name="check-circle"
                className="size-5"
              />
              Удалить
            </>
          ),
          onClick: async () => {
            if (!deleteApiDialog) return;

            try {
              await deleteAccountAsync(deleteApiDialog.id);
              toast.success("Аккаунт успешно удалён");

              if (selectedAccountUuid === deleteApiDialog.id) {
                setSelectedAccountUuid(null);
                await refetch();
              }

              setDeleteApiDialog(null);
            } catch (e) {
              toast.error(e instanceof Error ? e.message : "Ошибка при удалении аккаунта");
            }
          },
        }}
      />
    </div>
  );
};
